import React, { useEffect } from 'react';
import { Form, Input, Button, Card, Flex } from 'antd';
import { LabelWithDescription } from '@/components/LabelWithdescription';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { DeleteOutline } from '@mui/icons-material';
import { IBookingRequestFormContentProps } from '@/interfaces/ICalendarWorkflows';

const BookingRequestFormContent: React.FC<IBookingRequestFormContentProps> = ({
	form,
	isEditing,
	currentWorkflow,
	existingWorkflows,
	onSubmit,
	onCancel,
	mutation,
	getDefaultFormValues,
	getEditFormValues,
}) => {
	useEffect(() => {
		if (currentWorkflow) {
			setTimeout(() => form.setFieldsValue(getEditFormValues(currentWorkflow)), 100);
		} else {
			setTimeout(() => form.setFieldsValue(getDefaultFormValues()), 100);
		}
	}, [currentWorkflow, form, getDefaultFormValues, getEditFormValues]);

	const handleFormSubmit = async (values: any) => {
		await onSubmit(values, isEditing, currentWorkflow);
	};

	const scenarioNumber = existingWorkflows.length + 1;

	return (
		<div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px' }}>
			<Form form={form} layout="vertical" onFinish={handleFormSubmit} className="mt-4">
				<Form.List name="scenarios" initialValue={[{}]}>
					{(fields, { add, remove }) => (
						<>
							{fields.map(({ key, name, ...restField }, index) => (
								<Flex key={key} className="mb-2" gap={8}>
									<Card
										className="flex-1"
										title={
											<div className="flex justify-between">
												<span className="font-bold text-base">
													{isEditing
														? 'Edit Booking Request Scenario'
														: `Booking Request Scenario ${scenarioNumber + index}`}
												</span>
												{!isEditing && fields.length > 1 && (
													<Button
														type="primary"
														danger
														icon={<DeleteOutline />}
														onClick={() => remove(name)}
													>
														Delete
													</Button>
												)}
											</div>
										}
									>
										<div className="border-b pb-4 mb-4">
											<LabelWithDescription
												label="Scenario Description"
												description="Describe when you want to collect booking information from the caller."
											/>
											<Form.Item
												{...restField}
												name={[name, 'customScenario']}
												className="mt-2"
												rules={[
													{ required: true, message: 'Please provide a scenario description' },
												]}
											>
												<Input.TextArea
													placeholder="e.g. Collect caller availability when they want to schedule an appointment"
													autoSize={{ minRows: 2, maxRows: 4 }}
												/>
											</Form.Item>
										</div>

										<div className="border-b pb-4 mb-4">
											<LabelWithDescription
												label="Questions"
												description="Questions to collect availability and appointment preferences from the caller."
											/>
											<Form.List name={[name, 'questions']} initialValue={[{}]}>
												{(questionFields, { add: addQuestion, remove: removeQuestion }) => (
													<>
														{questionFields.map(({ key: qKey, name: qName, ...qRestField }) => (
															<Flex key={qKey} align="baseline" gap={8} className="mb-2">
																<Form.Item
																	className="flex-1"
																	{...qRestField}
																	name={[qName, 'question']}
																	rules={[
																		{
																			validator: (_, value) => {
																				if (!value || value.trim() === '') {
																					return Promise.reject("Question can't be blank");
																				}
																				return Promise.resolve();
																			},
																		},
																	]}
																>
																	<Input placeholder="e.g. What days of the week work best for you?" />
																</Form.Item>
																{questionFields.length > 1 && (
																	<MinusCircleOutlined onClick={() => removeQuestion(qName)} />
																)}
															</Flex>
														))}
														<Form.Item>
															<Button
																type="dashed"
																onClick={() => addQuestion()}
																block
																icon={<PlusOutlined />}
															>
																Add Question
															</Button>
														</Form.Item>
													</>
												)}
											</Form.List>
										</div>

										<div className="pb-4">
											<LabelWithDescription
												label="What should the AI do next?"
												description="Instructions for the AI to follow after collecting the booking information."
											/>
											<Form.Item
												{...restField}
												name={[name, 'postExecutionPrompt']}
												className="mt-2"
											>
												<Input.TextArea
													placeholder="e.g. Thank the caller and let them know someone will contact them within 24 hours to confirm appointment details."
													autoSize={{ minRows: 2, maxRows: 4 }}
												/>
											</Form.Item>
										</div>
									</Card>
								</Flex>
							))}

							{!isEditing && (
								<Form.Item>
									<Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
										Add Another Booking Scenario
									</Button>
								</Form.Item>
							)}
						</>
					)}
				</Form.List>

				<div className="flex justify-end gap-2 pt-4 border-t sticky bottom-0 bg-white">
					<Button onClick={onCancel}>Cancel</Button>
					<Button type="primary" htmlType="submit" loading={mutation?.isPending}>
						{isEditing ? 'Update Booking Request Workflow' : 'Add Booking Request Workflow'}
					</Button>
				</div>
			</Form>
		</div>
	);
};

export default BookingRequestFormContent;
